/**
 * Landmark vocabulary for the pose boundary (architecture §10, §25).
 *
 * These types deliberately live in `src/interaction/pose` and **not** in `src/shared`. Games may
 * import `src/shared`; if a landmark type were declared there, a game could name it in a
 * signature and the "games never see raw landmarks" invariant would rest on convention alone.
 * Keeping the vocabulary inside the boundary makes the invariant structural: only
 * `src/interaction/**` and `src/platform/**` can even spell these types.
 *
 * Nothing here is persisted. Landmarks are frame-scoped values handed to detector code and
 * dropped (architecture §19; privacy checklist rows 4, 6, 7).
 */

/**
 * A single landmark in normalised image space: `x`/`y` in `[0, 1]` relative to the source frame,
 * `z` roughly in the same scale with the hip midpoint as origin, `visibility` in `[0, 1]`.
 */
export interface PoseLandmarkPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly visibility: number;
}

/** Landmark indices used by the MediaPipe 33-point pose topology, named for the detectors. */
export const POSE_LANDMARK = {
  nose: 0,
  leftShoulder: 11,
  rightShoulder: 12,
  leftElbow: 13,
  rightElbow: 14,
  leftWrist: 15,
  rightWrist: 16,
  leftHip: 23,
  rightHip: 24,
  leftKnee: 25,
  rightKnee: 26,
  leftAnkle: 27,
  rightAnkle: 28,
} as const;

/** How many landmarks a MediaPipe pose carries. Used to reject a mis-shaped result. */
export const POSE_LANDMARK_COUNT = 33;

/**
 * The landmarks the Spider Math detectors actually depend on: lane position (shoulders/hips),
 * jump and crouch (hips/knees/ankles), hands raised (wrists vs shoulders), presence (nose).
 *
 * Confidence is derived from this subset rather than from all 33 points, so that an occluded
 * fingertip or foot does not drag down a detection the game never consults.
 */
export const CORE_POSE_LANDMARKS: readonly number[] = [
  POSE_LANDMARK.nose,
  POSE_LANDMARK.leftShoulder,
  POSE_LANDMARK.rightShoulder,
  POSE_LANDMARK.leftWrist,
  POSE_LANDMARK.rightWrist,
  POSE_LANDMARK.leftHip,
  POSE_LANDMARK.rightHip,
  POSE_LANDMARK.leftKnee,
  POSE_LANDMARK.rightKnee,
  POSE_LANDMARK.leftAnkle,
  POSE_LANDMARK.rightAnkle,
];

/** One detected body in one frame. */
export interface PoseObservation {
  /** Index of this pose within the frame. Lane identity is Phase 4's problem, not this one. */
  readonly poseIndex: number;
  /** 33 landmarks in normalised image space. */
  readonly landmarks: readonly PoseLandmarkPoint[];
  /** 33 landmarks in metric world space, hip midpoint as origin. */
  readonly worldLandmarks: readonly PoseLandmarkPoint[];
  /**
   * Mean visibility across {@link CORE_POSE_LANDMARKS}. This is the value a detector thresholds
   * under D-059 PS3-1: at or above threshold the adapter emits an ordinary semantic event; below
   * it the adapter emits nothing and records input uncertainty instead.
   */
  readonly coreConfidence: number;
  /** Lowest visibility among the core landmarks — an occlusion signal a mean would hide. */
  readonly minCoreVisibility: number;
}

/** The result of one detection call. Nothing else crosses the boundary. */
export interface PoseFrame {
  /**
   * Monotonic timestamp (ms) the frame was submitted with. MediaPipe's VIDEO mode requires
   * strictly increasing values, so this is the value actually handed to the task, which may be
   * nudged forward from the caller's timestamp.
   */
  readonly timestampMs: number;
  /** Wall-clock-ish reading (`performance.now()`) taken when detection returned. */
  readonly detectedAt: number;
  /** How long the detection call itself took, in ms. Seeds the PS3-6 performance tunables. */
  readonly detectionLatencyMs: number;
  /** Detected bodies, at most `numPoses`. Empty means nobody was found in this frame. */
  readonly poses: readonly PoseObservation[];
}

/** Which MediaPipe running mode the engine was created for. */
export type PoseRunningMode = 'IMAGE' | 'VIDEO';

/** Which backend MediaPipe should use for inference. */
export type PoseDelegate = 'GPU' | 'CPU';

/** Lifecycle state of the engine. */
export type PoseEngineStatus = 'idle' | 'loading' | 'ready' | 'closed' | 'failed';

/**
 * Same-origin locations of the model and WASM assets.
 *
 * Both must be self-hosted: the model and the WASM runtime are served from our own origin so that
 * a session never depends on, or discloses itself to, a CDN (architecture §23, §25 #7).
 */
export interface PoseEngineAssets {
  /** Directory containing `vision_wasm*_internal.{js,wasm}`, e.g. `/vendor/mediapipe/tasks-vision`. */
  readonly wasmBasePath: string;
  /** Path to the `.task` bundle, e.g. `/models/pose/pose_landmarker_full.task`. */
  readonly modelAssetPath: string;
}

/** Everything the engine needs to start. Numeric defaults live in `pose-assets.ts`. */
export interface PoseEngineOptions {
  readonly assets: PoseEngineAssets;
  readonly runningMode: PoseRunningMode;
  readonly delegate: PoseDelegate;
  readonly numPoses: number;
  readonly minPoseDetectionConfidence: number;
  readonly minPosePresenceConfidence: number;
  readonly minTrackingConfidence: number;
}

/** Anything MediaPipe can run detection over. Narrowed here so the engine's surface stays small. */
export type PoseImageSource =
  HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | ImageBitmap | OffscreenCanvas;
